import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { PriceHistoryRow } from "../api/articles";
import type { ProjectSummary } from "../api/projects";
import DocumentPdfActions from "./DocumentPdfActions";
import { formatDate, formatEuro, toNumber } from "../lib/format";
import { formatPercentAbove, priceAxis } from "../lib/priceAxis";
import { projectDisplay } from "../lib/projectDisplay";

type Point = {
  date: string;
  timestamp: number;
  net_unit_price: number;
  project_key: string | null;
  doc_type: string | null;
  doc_number: string | null;
  invoice_doc_id: string | null;
  line_pos: string | number | null;
  page_no: number | null;
  supplier: string | null;
  source_kind: string | null;
  previous_price: number | null;
};

type Props = {
  rows: PriceHistoryRow[];
  projectByKey?: ReadonlyMap<string, ProjectSummary>;
};

const TICK_COUNT = 6;

function computeTicks(min: number, max: number): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) return [min];
  const step = (max - min) / (TICK_COUNT - 1);
  return Array.from({ length: TICK_COUNT }, (_, i) =>
    Math.round(min + step * i),
  );
}

type ZoomState = {
  refStart: number | null;
  refEnd: number | null;
  left: number | null;
  right: number | null;
};

const INITIAL_ZOOM: ZoomState = {
  refStart: null,
  refEnd: null,
  left: null,
  right: null,
};

export default function PriceHistoryChart({ rows, projectByKey }: Props) {
  const [hideZeros, setHideZeros] = useState(true);
  const [pinnedTimestamp, setPinnedTimestamp] = useState<number | null>(null);

  const points = useMemo<Point[]>(() => {
    const out: Point[] = [];
    for (const row of rows) {
      const price = toNumber(row.net_unit_price);
      if (price === null || !row.doc_date) continue;
      if (hideZeros && price <= 0) continue;
      const timestamp = Date.parse(row.doc_date);
      if (!Number.isFinite(timestamp)) continue;
      out.push({
        date: row.doc_date.slice(0, 10),
        timestamp,
        net_unit_price: price,
        project_key: row.project_key,
        doc_type: row.doc_type,
        doc_number: row.doc_number,
        invoice_doc_id: row.invoice_doc_id,
        line_pos: row.line_pos,
        page_no: row.page_no,
        supplier: row.supplier,
        source_kind: row.source_kind ?? null,
        previous_price: null,
      });
    }
    out.sort((a, b) => a.timestamp - b.timestamp);
    // "Vorheriger Kauf" is the last price on an earlier day, not a same-day sibling.
    let previousDayPrice: number | null = null;
    let dayPrice: number | null = null;
    let day: number | null = null;
    for (const point of out) {
      if (point.timestamp !== day) {
        previousDayPrice = dayPrice ?? previousDayPrice;
        day = point.timestamp;
      }
      point.previous_price = previousDayPrice;
      dayPrice = point.net_unit_price;
    }
    return out;
  }, [rows, hideZeros]);

  const lowestPrice = useMemo(
    () => (points.length ? Math.min(...points.map((p) => p.net_unit_price)) : null),
    [points],
  );

  const pinnedPoints = useMemo(() => {
    if (pinnedTimestamp === null) return [];
    return points.filter((p) => p.timestamp === pinnedTimestamp);
  }, [points, pinnedTimestamp]);

  useEffect(() => {
    if (pinnedTimestamp === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPinnedTimestamp(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pinnedTimestamp]);

  const [zoom, setZoom] = useState<ZoomState>(INITIAL_ZOOM);

  const fullMin = points[0]?.timestamp ?? 0;
  const fullMax = points[points.length - 1]?.timestamp ?? 0;

  const activeLeft = zoom.left ?? fullMin;
  const activeRight = zoom.right ?? fullMax;
  const isZoomed = zoom.left !== null && zoom.right !== null;

  const ticks = useMemo(
    () => computeTicks(activeLeft, activeRight),
    [activeLeft, activeRight],
  );
  const yAxis = useMemo(
    () =>
      priceAxis(
        points
          .filter((p) => p.timestamp >= activeLeft && p.timestamp <= activeRight)
          .map((p) => p.net_unit_price),
      ),
    [points, activeLeft, activeRight],
  );

  const handleMouseDown = (e: unknown) => {
    const next = (e as { activeLabel?: number } | null)?.activeLabel;
    if (typeof next !== "number") return;
    setZoom((prev) => ({ ...prev, refStart: next, refEnd: next }));
  };

  const handleMouseMove = (e: unknown) => {
    const next = (e as { activeLabel?: number } | null)?.activeLabel;
    if (typeof next !== "number") return;
    setZoom((prev) =>
      prev.refStart === null ? prev : { ...prev, refEnd: next },
    );
  };

  const handleMouseUp = () => {
    setZoom((prev) => {
      if (prev.refStart === null || prev.refEnd === null) return prev;
      if (prev.refStart === prev.refEnd) {
        const ts = prev.refStart;
        setPinnedTimestamp((current) => (current === ts ? null : ts));
        return { ...prev, refStart: null, refEnd: null };
      }
      const [left, right] =
        prev.refStart < prev.refEnd
          ? [prev.refStart, prev.refEnd]
          : [prev.refEnd, prev.refStart];
      return { refStart: null, refEnd: null, left, right };
    });
  };

  const resetZoom = () => setZoom(INITIAL_ZOOM);

  if (points.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500">
        Keine Preisdaten mit Datum vorhanden.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
        <span>
          {isZoomed
            ? `Zoom: ${formatDate(new Date(activeLeft).toISOString())} – ${formatDate(new Date(activeRight).toISOString())}`
            : "Klick auf einen Punkt fixiert die Auswahl. Ziehen vergrößert den Zeitraum."}
        </span>
        <div className="flex items-center gap-3">
          <label className="flex cursor-pointer items-center gap-1.5 text-slate-600">
            <input
              type="checkbox"
              checked={hideZeros}
              onChange={(e) => setHideZeros(e.target.checked)}
              className="accent-emerald-600"
            />
            Nur mit Preis
          </label>
          <button
            type="button"
            onClick={resetZoom}
            disabled={!isZoomed}
            className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Zoom zurücksetzen
          </button>
        </div>
      </div>
      <div className="relative h-72 w-full select-none">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={points}
            margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
            <XAxis
              dataKey="timestamp"
              type="number"
              domain={[activeLeft, activeRight]}
              allowDataOverflow
              scale="time"
              ticks={ticks}
              tickFormatter={(v: number) =>
                formatDate(new Date(v).toISOString())
              }
              stroke="#64748b"
              fontSize={12}
              minTickGap={32}
              padding={{ left: 16, right: 16 }}
            />
            <YAxis
              stroke="#64748b"
              fontSize={12}
              tickFormatter={(v: number) => formatEuro(v)}
              width={80}
              domain={yAxis?.domain ?? ["auto", "auto"]}
              ticks={yAxis?.ticks}
              allowDataOverflow={false}
            />
            <Tooltip
              labelFormatter={(label) =>
                typeof label === "number"
                  ? formatDate(new Date(label).toISOString())
                  : String(label ?? "")
              }
              formatter={(value, _name, item) => {
                const payload = (item as { payload?: Point } | undefined)
                  ?.payload;
                const parts = [formatEuro(value as number)];
                const aboveLowest = formatPercentAbove(value as number, lowestPrice);
                if (aboveLowest) parts.push(`${aboveLowest} zum niedrigsten Preis`);
                const sincePrevious = formatPercentAbove(value as number, payload?.previous_price);
                if (sincePrevious) parts.push(`${sincePrevious} zum vorigen Kauf`);
                if (payload?.project_key) {
                  const project = projectByKey?.get(payload.project_key);
                  const display = projectDisplay(project, payload.project_key);
                  parts.push(display?.title ?? payload.project_key);
                }
                if (payload?.doc_type) parts.push(payload.doc_type);
                return [parts.join(" · "), "Preis"];
              }}
              contentStyle={{ fontSize: 12 }}
            />
            <Line
              type="monotone"
              dataKey="net_unit_price"
              stroke="#0f766e"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            {pinnedTimestamp !== null ? (
              <ReferenceLine
                x={pinnedTimestamp}
                stroke="#0f766e"
                strokeDasharray="4 4"
                strokeOpacity={0.6}
              />
            ) : null}
            {zoom.refStart !== null && zoom.refEnd !== null ? (
              <ReferenceArea
                x1={zoom.refStart}
                x2={zoom.refEnd}
                strokeOpacity={0.3}
                fill="#0f766e"
                fillOpacity={0.08}
              />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
        {pinnedTimestamp !== null && pinnedPoints.length > 0 ? (
          <PinnedCard
            timestamp={pinnedTimestamp}
            points={pinnedPoints}
            lowestPrice={lowestPrice}
            projectByKey={projectByKey}
            onClose={() => setPinnedTimestamp(null)}
          />
        ) : null}
      </div>
    </div>
  );
}

function PinnedCard({
  timestamp,
  points,
  lowestPrice,
  projectByKey,
  onClose,
}: {
  timestamp: number;
  points: Point[];
  lowestPrice: number | null;
  projectByKey?: ReadonlyMap<string, ProjectSummary>;
  onClose: () => void;
}) {
  return (
    <div className="absolute right-3 top-3 z-10 max-h-[16rem] w-72 max-w-[90%] overflow-y-auto rounded-md border border-slate-200 bg-white p-3 shadow-lg">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">
          {formatDate(new Date(timestamp).toISOString())}
          <span className="ml-2 font-normal normal-case text-slate-400">
            {points.length} {points.length === 1 ? "Eintrag" : "Einträge"}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Auswahl lösen"
          className="text-slate-400 hover:text-slate-600"
        >
          ×
        </button>
      </div>
      <ul className="space-y-2 text-xs">
        {points.map((p, i) => {
          const project = p.project_key
            ? projectByKey?.get(p.project_key)
            : undefined;
          const display = projectDisplay(project, p.project_key);
          const projectLabel = display?.meta
            ? `${display.title} · ${display.meta}`
            : display?.title;
          return (
            <li
              key={`${p.invoice_doc_id ?? "doc"}-${i}`}
              className="rounded border border-slate-100 bg-slate-50 p-2"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium text-slate-800">
                  {formatEuro(p.net_unit_price)}
                  {formatPercentAbove(p.net_unit_price, lowestPrice) ? (
                    <span className="ml-1.5 font-normal text-slate-500">
                      {formatPercentAbove(p.net_unit_price, lowestPrice)} zum niedrigsten Preis
                    </span>
                  ) : null}
                </span>
                {p.doc_type ? (
                  <span className="text-[10px] uppercase tracking-wide text-slate-500">
                    {p.doc_type}
                  </span>
                ) : null}
              </div>
              {projectLabel ? (
                <div className="mt-1 truncate text-slate-600" title={display?.tooltip}>
                  {projectLabel}
                </div>
              ) : null}
              {p.supplier ? (
                <div className="text-slate-500">{p.supplier}</div>
              ) : null}
              {p.invoice_doc_id ? (
                <DocumentPdfActions
                  docId={p.invoice_doc_id}
                  docNumber={p.doc_number}
                  sourceKind={p.source_kind}
                  linePos={p.line_pos}
                  pageNo={p.page_no}
                  className="mt-1"
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
